import { useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@staff-app/ui';
import { OPTICLOCK_COLORS } from '@staff-app/shared-types';
import type { OpticlockProject } from '@staff-app/shared-types';
import { ProjectDot, projectColorClass } from '../lib/project-colors';
import {
  useOpticlockAllProjects,
  useSetOpticlockProjectArchived,
  useUpsertOpticlockProject,
} from '../hooks/useOpticlockProjects';


/**
 * Root's project-category admin, below the rollup on the Time Dashboard page.
 *
 * THERE IS NO DELETE CONTROL, and that is deliberate: entries reference a project forever, so
 * removing one would either orphan history or silently rewrite it. Archiving takes a category
 * out of the agents' picker while every entry already filed against it keeps counting. The API
 * offers no delete mutation either, so this is not merely a UI omission.
 */
export function ProjectCategoriesSection() {
  const { data, loading, error } = useOpticlockAllProjects();
  const [upsert, upsertState] = useUpsertOpticlockProject();
  const [setArchived] = useSetOpticlockProjectArchived();

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('slate');
  const [sortOrder, setSortOrder] = useState('0');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const projects = data?.opticlockProjects ?? [];

  async function add(): Promise<void> {
    if (!name.trim()) return;
    try {
      await upsert({
        variables: { input: { name: name.trim(), color, sortOrder: Number(sortOrder) || 0 } },
      });
      toast.success(`Added ${name.trim()}`);
      setName('');
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not add that project');
    }
  }

  async function rename(p: OpticlockProject): Promise<void> {
    try {
      await upsert({
        variables: {
          input: { id: p._id, name: editName.trim(), color: p.color ?? undefined, sortOrder: p.sortOrder },
        },
      });
      toast.success('Project renamed');
      setEditingId(null);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not rename that project');
    }
  }

  async function toggleArchived(p: OpticlockProject): Promise<void> {
    try {
      await setArchived({ variables: { id: p._id, archived: !p.archived } });
      toast.success(p.archived ? `${p.name} restored` : `${p.name} archived`);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not change that project');
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid="opticlock-projects-section">
      <h2 className="text-sm font-semibold">{'Project categories'}</h2>
      <p className="max-w-3xl text-xs text-muted-foreground">
        {
          'Only you can change these. Agents pick from the live list — they cannot type a new project name, which is how the old workspace ended up with both “Testing” and “Toast Testing”. Archive retires a category without touching the time already logged against it.'
        }
      </p>

      {error ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="opticlock-project-error"
        >
          {error.message}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex grow flex-col gap-1">
            <Label className="text-xs text-muted-foreground">{'Name'}</Label>
            <Input
              value={name}
              placeholder="e.g. Sales Demo Support"
              aria-label="Project name"
              data-testid="opticlock-project-name"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">{'Colour'}</Label>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger className="w-[150px]" data-testid="opticlock-project-color">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPTICLOCK_COLORS.map((c) => (
                  <SelectItem key={c} value={c}>
                    <span className="flex items-center gap-2">
                      <span className={cn('h-2 w-2 rounded-full', projectColorClass(c))} />
                      {c}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">{'Order'}</Label>
            <Input
              type="number"
              className="w-[90px]"
              value={sortOrder}
              aria-label="Sort order"
              data-testid="opticlock-project-sort"
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </div>
          <Button
            disabled={!name.trim() || upsertState.loading}
            data-testid="opticlock-project-add"
            onClick={add}
          >
            {upsertState.loading ? 'Adding…' : 'Add project'}
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{'Project'}</TableHead>
            <TableHead>{'Slug'}</TableHead>
            <TableHead>{'Order'}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && projects.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-xs text-muted-foreground">
                {'Loading…'}
              </TableCell>
            </TableRow>
          ) : null}
          {!loading && projects.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-xs text-muted-foreground">
                {'No categories yet — add the first one above.'}
              </TableCell>
            </TableRow>
          ) : null}

          {projects.map((p) => (
            <TableRow
              key={p._id}
              className={cn(p.archived && 'opacity-60')}
              data-testid={`opticlock-project-row-${p._id}`}
            >
              <TableCell>
                {editingId === p._id ? (
                  <Input
                    value={editName}
                    aria-label="Project name"
                    onChange={(e) => setEditName(e.target.value)}
                  />
                ) : (
                  <span className="flex items-center gap-2">
                    <ProjectDot color={p.color} />
                    <span className="text-sm">{p.name}</span>
                    {p.archived ? <Badge variant="secondary">{'Archived'}</Badge> : null}
                  </span>
                )}
              </TableCell>
              {/* The slug is shown because it is the id the external consumer keys on — and it
                  never changes when the name does. */}
              <TableCell className="font-mono text-xs text-muted-foreground">{p._id}</TableCell>
              <TableCell className="tabular-nums">{p.sortOrder}</TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  {editingId === p._id ? (
                    <>
                      <Button
                        size="sm"
                        data-testid={`opticlock-project-row-${p._id}-save`}
                        onClick={() => rename(p)}
                      >
                        {'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`opticlock-project-row-${p._id}-cancel`}
                        onClick={() => setEditingId(null)}
                      >
                        {'Cancel'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`opticlock-project-row-${p._id}-edit`}
                        onClick={() => {
                          setEditingId(p._id);
                          setEditName(p.name);
                        }}
                      >
                        {'Rename'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`opticlock-project-row-${p._id}-${p.archived ? 'unarchive' : 'archive'}`}
                        onClick={() => toggleArchived(p)}
                      >
                        {p.archived ? 'Restore' : 'Archive'}
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
